/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { Dialog } from "../../../dialogs/Dialog";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { RenameDialogComponent } from "./RenameDialogComponent";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { createEventEmitter } from "@adapptio/lib-shared";

export interface RenameDialog_Params {
	resourceName?: string;
}

export interface RenameDialog_Result {
	newName: string;
}

export class RenameDialog extends Dialog<RenameDialog_Params, RenameDialog_Result> {
	private resourceService: ResourceService;

	private saveInProgress = false;

	/** Event emitted when save progress changes */
	public onSaveStateChange = createEventEmitter<void>();

	/**
	 * Dialog constructor
	 *
	 * @param dc Dependency container
	 * @param id Dialog ID
	 * @param params Open params
	 */
	public constructor(dc: IEditor, id: string, params: RenameDialog_Params) {
		super(dc, id, params);

		this.resourceService = this.dc.get("resourceService");
	}

	public getResourceService(): ResourceService {
		return this.resourceService;
	}

	public getComponent(): typeof RenameDialogComponent {
		return RenameDialogComponent;
	}

	/**
	 * Returns if a resource with given basePath and name already exists
	 *
	 * @param basePath Base path
	 * @param name Name
	 */
	public checkIfResourceExists(basePath: string, name: string): boolean {
		return !!this.getResourceService().getResourceByPath(normalizeResourcePath(basePath, name));
	}
}
